// src/theme/index.ts
import { createTheme, alpha, Shadows } from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";

// Brand Colors
const brandColors = {
  primary: {
    50: "#E5F0FF",
    100: "#B8D7FF",
    200: "#8ABEFF",
    300: "#5CA5FF",
    400: "#2E8CFF",
    500: "#0073FF", // main
    600: "#005CD9",
    700: "#0045B3",
    800: "#002E8C",
    900: "#001766",
  },
  secondary: {
    50: "#F2EEFF",
    100: "#D8D1FF",
    200: "#BEB4FF",
    300: "#A497FF",
    400: "#8A7AFF",
    500: "#705DFF", // main
    600: "#5A44FF",
    700: "#432BD9",
    800: "#2C12B3",
    900: "#15008C",
  },
};

// Common colors and settings for both themes
const common = {
  primary: {
    main: brandColors.primary[500],
    light: brandColors.primary[300],
    dark: brandColors.primary[700],
    contrastText: "#FFFFFF",
    ...brandColors.primary,
  },
  secondary: {
    main: brandColors.secondary[500],
    light: brandColors.secondary[300],
    dark: brandColors.secondary[700],
    contrastText: "#FFFFFF",
    ...brandColors.secondary,
  },
  error: {
    light: "#FF6B6B",
    main: "#FA5252",
    dark: "#E03131",
    contrastText: "#FFFFFF",
  },
  warning: {
    light: "#FFD43B",
    main: "#FCC419",
    dark: "#F08C00",
    contrastText: "#000000",
  },
  info: {
    light: "#74C0FC",
    main: "#339AF0",
    dark: "#1C7ED6",
    contrastText: "#FFFFFF",
  },
  success: {
    light: "#69DB7C",
    main: "#40C057",
    dark: "#2B8A3E",
    contrastText: "#FFFFFF",
  },
};

// Typography settings
const typography: TypographyOptions = {
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  h1: {
    fontSize: "3.5rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
  },
  h2: {
    fontSize: "2.75rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  h3: {
    fontSize: "2.25rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  h4: {
    fontSize: "1.75rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h5: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: "1.125rem",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: "1rem",
    lineHeight: 1.6,
  },
  body2: {
    fontSize: "0.875rem",
    lineHeight: 1.6,
  },
  button: {
    fontSize: "0.875rem",
    fontWeight: 600,
    textTransform: "none",
  },
  caption: {
    fontSize: "0.75rem",
    lineHeight: 1.5,
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
};

// Component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: "8px",
        padding: "8px 16px",
        transition: "all 0.2s ease-in-out",
      },
      contained: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none",
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: "12px",
        transition: "all 0.2s ease-in-out",
      },
    },
  },
};

// Light theme
export const theme = createTheme({
  palette: {
    mode: "light",
    ...common,
    background: {
      default: "#F8F9FC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1F36",
      secondary: "#424770",
      disabled: "#697386",
    },
    divider: alpha("#000000", 0.08),
    action: {
      active: alpha("#000000", 0.6),
      hover: alpha("#000000", 0.04),
      selected: alpha("#000000", 0.08),
      disabled: alpha("#000000", 0.26),
      disabledBackground: alpha("#000000", 0.12),
    },
  },
  typography,
  components,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 1px 2px rgba(0, 0, 0, 0.05)",
    "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
    "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0px 25px 30px -5px rgba(0, 0, 0, 0.1), 0px 12px 12px -5px rgba(0, 0, 0, 0.04)",
    ...Array(18).fill("none"), // Fill remaining shadows to reach exactly 25 elements
  ] as Shadows,
});

// Dark theme
export const darkTheme = createTheme({
  ...theme,
  palette: {
    mode: "dark",
    ...common,
    background: {
      default: "#0A1929",
      paper: "#132F4C",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B2BAC2",
      disabled: "#6F7E8C",
    },
    divider: alpha("#FFFFFF", 0.08),
    action: {
      active: alpha("#FFFFFF", 0.6),
      hover: alpha("#FFFFFF", 0.04),
      selected: alpha("#FFFFFF", 0.08),
      disabled: alpha("#FFFFFF", 0.26),
      disabledBackground: alpha("#FFFFFF", 0.12),
    },
  },
  components: {
    ...components,
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#132F4C",
          borderRadius: "12px",
        },
      },
    },
  },
});

// Theme with toggle functionality
export const getTheme = (mode: "light" | "dark") =>
  mode === "light" ? theme : darkTheme;
